import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Form, Input, Button, Alert } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { authService } from '../services/auth';
import { useBranding } from '../hooks/useBranding';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common');
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/app/dashboard';
  const [loading, setLoading] = React.useState(false);
  const [authMessage, setAuthMessage] = React.useState<string | null>(null);
  const { branding } = useBranding();

  useEffect(() => {
    const savedMessage = localStorage.getItem('authMessage');
    if (savedMessage) {
      setAuthMessage(savedMessage);
      localStorage.removeItem('authMessage');
    }
  }, []);

  useEffect(() => {
    document.title = branding.brandName;
  }, [branding.brandName]);


  const onFinish = async (values: { email: string; password: string }) => {
    setLoading(true);
    setAuthMessage(null);
    try {
      await authService.login(values);
      navigate(from, { replace: true });
    } catch (error: any) {
      const errorMessage = t('login.failed');
      setAuthMessage(errorMessage);
      if (error.response?.data?.error) {
        console.error('Login error code:', error.response.data.error);
      }
    } finally {
      setLoading(false);
    }
  };

  const pc = branding.primaryColor;

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: '#0d0f14',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      position: 'relative',
      overflow: 'hidden'
    }}>

      {/* Animated gradient orbs */}
      <div className="login-orb login-orb-1" style={{ background: `radial-gradient(circle, ${pc}35 0%, transparent 65%)` }} />
      <div className="login-orb login-orb-2" style={{ background: `radial-gradient(circle, ${pc}20 0%, transparent 65%)` }} />
      <div className="login-orb login-orb-3" style={{ background: 'radial-gradient(circle, rgba(100,60,180,0.18) 0%, transparent 65%)' }} />

      {/* Perspective grid floor */}
      <div className="login-grid-perspective" />

      {/* Vertical grid overlay — more visible */}
      <div style={{
        position: 'fixed',
        inset: 0,
        backgroundImage: `linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)`,
        backgroundSize: '50px 50px',
        pointerEvents: 'none',
        zIndex: 1
      }} />

      {/* Diagonal wireframe lines — boosted visibility */}
      <svg className="login-wireframe" viewBox="0 0 1440 900" fill="none" xmlns="http://www.w3.org/2000/svg">
        <line x1="0" y1="900" x2="500" y2="200" stroke={pc} strokeOpacity="0.15" strokeWidth="1"/>
        <line x1="200" y1="900" x2="700" y2="100" stroke={pc} strokeOpacity="0.1" strokeWidth="1"/>
        <line x1="400" y1="900" x2="850" y2="50" stroke={pc} strokeOpacity="0.06" strokeWidth="1"/>
        <line x1="1440" y1="900" x2="900" y2="150" stroke={pc} strokeOpacity="0.15" strokeWidth="1"/>
        <line x1="1240" y1="900" x2="750" y2="50" stroke={pc} strokeOpacity="0.1" strokeWidth="1"/>
        <line x1="1050" y1="900" x2="600" y2="0" stroke={pc} strokeOpacity="0.06" strokeWidth="1"/>
        <line x1="0" y1="600" x2="1440" y2="300" stroke={pc} strokeOpacity="0.08" strokeWidth="1"/>
        <line x1="0" y1="400" x2="1440" y2="500" stroke={pc} strokeOpacity="0.08" strokeWidth="1"/>
        <line x1="0" y1="200" x2="1440" y2="700" stroke={pc} strokeOpacity="0.05" strokeWidth="1"/>
        {/* Geometric shapes — hexagon outlines */}
        <polygon points="150,120 200,90 250,120 250,170 200,200 150,170" stroke={pc} strokeOpacity="0.18" strokeWidth="1" fill="none"/>
        <polygon points="1200,600 1260,565 1320,600 1320,660 1260,695 1200,660" stroke={pc} strokeOpacity="0.15" strokeWidth="1" fill="none"/>
        <polygon points="100,550 140,525 180,550 180,590 140,615 100,590" stroke={pc} strokeOpacity="0.12" strokeWidth="1" fill="none"/>
        <polygon points="1300,150 1340,125 1380,150 1380,190 1340,215 1300,190" stroke={pc} strokeOpacity="0.16" strokeWidth="1" fill="none"/>
        <polygon points="350,700 400,670 450,700 450,750 400,780 350,750" stroke={pc} strokeOpacity="0.1" strokeWidth="1" fill="none"/>
        {/* Rectangles — blueprint style */}
        <rect x="80" y="300" width="120" height="80" stroke={pc} strokeOpacity="0.12" strokeWidth="1" fill="none" rx="2"/>
        <rect x="1250" y="350" width="100" height="60" stroke={pc} strokeOpacity="0.1" strokeWidth="1" fill="none" rx="2"/>
        <rect x="60" y="650" width="80" height="50" stroke={pc} strokeOpacity="0.08" strokeWidth="1" fill="none" rx="2"/>
        <rect x="1320" y="500" width="90" height="70" stroke={pc} strokeOpacity="0.08" strokeWidth="1" fill="none" rx="2"/>
        {/* Diamonds */}
        <polygon points="720,50 760,90 720,130 680,90" stroke={pc} strokeOpacity="0.16" strokeWidth="1" fill="none"/>
        <polygon points="1100,750 1130,780 1100,810 1070,780" stroke={pc} strokeOpacity="0.1" strokeWidth="1" fill="none"/>
        {/* Triangle */}
        <polygon points="1350,80 1400,160 1300,160" stroke={pc} strokeOpacity="0.12" strokeWidth="1" fill="none"/>
        <polygon points="50,420 90,480 10,480" stroke={pc} strokeOpacity="0.08" strokeWidth="1" fill="none"/>
        {/* Small crosses / nodes */}
        <line x1="350" y1="140" x2="350" y2="160" stroke={pc} strokeOpacity="0.25" strokeWidth="1.5"/>
        <line x1="340" y1="150" x2="360" y2="150" stroke={pc} strokeOpacity="0.25" strokeWidth="1.5"/>
        <line x1="1100" y1="240" x2="1100" y2="260" stroke={pc} strokeOpacity="0.25" strokeWidth="1.5"/>
        <line x1="1090" y1="250" x2="1110" y2="250" stroke={pc} strokeOpacity="0.25" strokeWidth="1.5"/>
        <line x1="900" y1="700" x2="900" y2="720" stroke={pc} strokeOpacity="0.2" strokeWidth="1.5"/>
        <line x1="890" y1="710" x2="910" y2="710" stroke={pc} strokeOpacity="0.2" strokeWidth="1.5"/>
        <line x1="200" y1="450" x2="200" y2="470" stroke={pc} strokeOpacity="0.2" strokeWidth="1.5"/>
        <line x1="190" y1="460" x2="210" y2="460" stroke={pc} strokeOpacity="0.2" strokeWidth="1.5"/>
        {/* Dots / nodes */}
        <circle cx="500" cy="200" r="3" fill={pc} fillOpacity="0.3"/>
        <circle cx="700" cy="100" r="2.5" fill={pc} fillOpacity="0.25"/>
        <circle cx="900" cy="150" r="3" fill={pc} fillOpacity="0.3"/>
        <circle cx="750" cy="50" r="2.5" fill={pc} fillOpacity="0.25"/>
        <circle cx="350" cy="150" r="3.5" fill={pc} fillOpacity="0.35"/>
        <circle cx="1100" cy="250" r="3.5" fill={pc} fillOpacity="0.35"/>
        <circle cx="200" cy="460" r="3" fill={pc} fillOpacity="0.3"/>
        <circle cx="900" cy="710" r="3" fill={pc} fillOpacity="0.25"/>
      </svg>

      {/* Floating geometric shapes — CSS animated */}
      <div className="login-shape login-shape-1" style={{ borderColor: `${pc}30` }} />
      <div className="login-shape login-shape-2" style={{ borderColor: `${pc}20` }} />
      <div className="login-shape login-shape-3" style={{ borderColor: `${pc}25` }} />

      {/* Fade vignette at edges — softer */}
      <div style={{
        position: 'fixed',
        inset: 0,
        background: 'radial-gradient(ellipse at center, transparent 45%, #0d0f14 85%)',
        pointerEvents: 'none',
        zIndex: 2
      }} />

      {/* Card */}
      <div style={{
        position: 'relative',
        zIndex: 10,
        width: 420,
        padding: '48px 40px',
        background: 'rgba(14, 14, 22, 0.85)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 20,
        boxShadow: `0 0 80px ${pc}10, 0 24px 64px rgba(0,0,0,0.6)`
      }}>

        {/* Top accent line */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 120,
          height: 2,
          background: `linear-gradient(90deg, transparent, ${pc}, transparent)`,
          borderRadius: 2
        }} />

        {/* Brand logo + name */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          {branding.logoUrl && (
            <img
              src={branding.logoUrl}
              alt={branding.brandName}
              style={{ maxHeight: 52, maxWidth: 180, marginBottom: 20, display: 'block', margin: '0 auto 20px' }}
            />
          )}
          <div style={{
            fontSize: 28,
            fontWeight: 700,
            color: '#ffffff',
            letterSpacing: '-0.5px',
            lineHeight: 1.2
          }}>
            {branding.brandName}
          </div>
          <div style={{
            fontSize: 14,
            color: '#52525b',
            marginTop: 8,
            fontWeight: 400
          }}>
            {t('login.welcomeBack')}
          </div>
        </div>

        {authMessage && (
          <Alert
            message={<span style={{ color: '#fca5a5' }}>{authMessage}</span>}
            type="error"
            showIcon
            closable
            onClose={() => setAuthMessage(null)}
            style={{
              marginBottom: 24,
              borderRadius: 10,
              background: 'rgba(220,38,38,0.1)',
              border: '1px solid rgba(220,38,38,0.25)',
              color: '#fca5a5'
            }}
          />
        )}

        <Form name="login" onFinish={onFinish} scrollToFirstError layout="vertical">
          <Form.Item
            name="email"
            label={<span style={{ color: '#71717a', fontSize: 13, fontWeight: 500 }}>{t('login.email')}</span>}
            rules={[
              { required: true, message: t('login.emailRequired') }
            ]}
            style={{ marginBottom: 20 }}
          >
            <Input
              prefix={<UserOutlined style={{ color: '#3f3f46' }} />}
              placeholder={t('login.emailPlaceholder')}
              size="large"
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 10,
                color: '#f4f4f5',
                fontSize: 14,
                height: 46
              }}
            />
          </Form.Item>

          <Form.Item
            name="password"
            label={<span style={{ color: '#71717a', fontSize: 13, fontWeight: 500 }}>{t('login.password')}</span>}
            rules={[{ required: true, message: t('login.passwordRequired') }]}
            style={{ marginBottom: 32 }}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: '#3f3f46' }} />}
              placeholder={t('login.passwordPlaceholder')}
              size="large"
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 10,
                color: '#f4f4f5',
                fontSize: 14,
                height: 46
              }}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              htmlType="submit"
              block
              size="large"
              loading={loading}
              style={{
                background: `linear-gradient(135deg, ${pc}, ${pc}cc)`,
                borderRadius: 10,
                height: 46,
                fontSize: 15,
                fontWeight: 600,
                color: '#ffffff',
                border: 'none',
                boxShadow: `0 4px 24px ${pc}35`,
                transition: 'all 0.2s ease'
              }}
            >
              {t('login.submit')}
            </Button>
          </Form.Item>
        </Form>

        {/* Powered by line */}
        <div style={{
          textAlign: 'center',
          marginTop: 32,
          fontSize: 11,
          color: '#2a2a35',
          letterSpacing: '0.5px'
        }}>
          System Flowship
        </div>
      </div>

      <style>{`
        @keyframes orbFloat1 {
          0%, 100% { transform: translate(-30%, -40%) scale(1); }
          50% { transform: translate(-35%, -45%) scale(1.15); }
        }
        @keyframes orbFloat2 {
          0%, 100% { transform: translate(20%, 30%) scale(1); }
          50% { transform: translate(25%, 25%) scale(1.2); }
        }
        @keyframes orbFloat3 {
          0%, 100% { transform: translate(-60%, 20%) scale(1); }
          50% { transform: translate(-55%, 15%) scale(1.1); }
        }
        @keyframes shapeRotate1 {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes shapeRotate2 {
          0% { transform: rotate(45deg); }
          100% { transform: rotate(405deg); }
        }
        @keyframes shapeFloat {
          0%, 100% { transform: rotate(30deg) translateY(0); }
          50% { transform: rotate(30deg) translateY(-20px); }
        }
        .login-orb {
          position: fixed;
          border-radius: 50%;
          pointer-events: none;
          z-index: 0;
        }
        .login-orb-1 {
          width: 700px;
          height: 700px;
          top: 20%;
          right: 10%;
          animation: orbFloat1 12s ease-in-out infinite;
        }
        .login-orb-2 {
          width: 500px;
          height: 500px;
          bottom: 10%;
          left: 15%;
          animation: orbFloat2 15s ease-in-out infinite;
        }
        .login-orb-3 {
          width: 600px;
          height: 600px;
          top: 50%;
          left: 50%;
          animation: orbFloat3 18s ease-in-out infinite;
        }
        .login-wireframe {
          position: fixed;
          inset: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 1;
        }
        .login-grid-perspective {
          position: fixed;
          bottom: -20%;
          left: -10%;
          right: -10%;
          height: 60%;
          background:
            linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px);
          background-size: 80px 80px;
          transform: perspective(500px) rotateX(55deg);
          transform-origin: center bottom;
          pointer-events: none;
          z-index: 0;
          mask-image: linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 80%);
          -webkit-mask-image: linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 80%);
        }
        .login-shape {
          position: fixed;
          pointer-events: none;
          z-index: 1;
          border: 1px solid;
        }
        .login-shape-1 {
          width: 200px;
          height: 200px;
          top: 8%;
          left: 5%;
          border-radius: 0;
          animation: shapeRotate1 60s linear infinite;
          opacity: 0.6;
        }
        .login-shape-2 {
          width: 150px;
          height: 150px;
          bottom: 12%;
          right: 8%;
          border-radius: 50%;
          animation: shapeRotate2 45s linear infinite;
          opacity: 0.5;
        }
        .login-shape-3 {
          width: 100px;
          height: 180px;
          top: 25%;
          right: 18%;
          border-radius: 0;
          animation: shapeFloat 8s ease-in-out infinite;
          opacity: 0.4;
        }
        .ant-input,
        .ant-input-affix-wrapper {
          background: rgba(255,255,255,0.03) !important;
          border-color: rgba(255,255,255,0.08) !important;
          color: #f4f4f5 !important;
        }
        .ant-input-affix-wrapper:hover,
        .ant-input-affix-wrapper:focus,
        .ant-input-affix-wrapper-focused {
          border-color: ${pc}80 !important;
          box-shadow: 0 0 0 3px ${pc}15 !important;
        }
        .ant-input:hover,
        .ant-input:focus {
          border-color: ${pc}80 !important;
          box-shadow: 0 0 0 3px ${pc}15 !important;
        }
        .ant-input::placeholder {
          color: #3f3f46 !important;
        }
        .ant-input-password-icon {
          color: #3f3f46 !important;
        }
        .ant-input-password-icon:hover {
          color: #71717a !important;
        }
        .ant-form-item-explain-error {
          color: #f87171 !important;
          font-size: 12px !important;
        }
        .ant-form-item-label > label {
          color: #71717a !important;
        }
        .ant-alert-error .ant-alert-icon {
          color: #f87171 !important;
        }
        .ant-alert-error .ant-alert-close-icon {
          color: #f87171 !important;
        }
      `}</style>
    </div>
  );
};

export default Login;
